/**
 * Full-screen overlays that interrupt play: the Drift event card, the sealed-bid screens,
 * region scoring, the inspect card, the scoreboard and the final table.
 *
 * The sealed-bid overlay is the most important thing in this file. It is the one moment
 * where all four controllers are live at once, so it has to make four simultaneous
 * hidden decisions legible: each seat gets its own column, you can see *that* someone has
 * locked in but not *what* they chose, and the reveal is a single synchronised beat.
 */

import { CHARTERS } from '../game/charters';
import { eventCard } from '../game/events';
import { RELICS, REGIONS, SYSTEM_CLASSES, PLAYER_SHORT } from '../game/constants';
import { pendingBidders } from '../game/engine';
import { fleetsAtOwnedBy, isBlocked, regionStanding, systemOf } from '../game/queries';
import { finalStandings, type FinalStanding } from '../game/scoring';
import type {
  BidRound, GameState, HexKey, PlayerId, StarSystem,
} from '../game/types';
import { HUD_LAYOUT } from './hud';
import { ease, INK, seatColor, TYPE, withAlpha } from './palette';
import {
  chip, hintBar, meter, panel, paragraph, resourceRow, roundRect, seatBadge, text,
} from './widgets';
import { drawHabitability, drawHazard, drawRelic, drawYieldPips } from './sprites';

/** Per-seat bid draft, held by the UI while bids are being entered. */
export interface BidDraft {
  amount: number;
  locked: boolean;
}

export interface OverlayView {
  /** Bid amounts each seat is currently dialling, before locking. */
  drafts: Record<PlayerId, BidDraft>;
  /** 0..1 progress of the reveal animation. */
  revealProgress: number;
  /** Scoreboard toggled with Back. */
  showScoreboard: boolean;
  /** Inspect card for a system. */
  inspect: HexKey | null;
  /** Event card presentation timer, 0..1; 1 means fully shown. */
  eventProgress: number;
}

export function emptyOverlayView(): OverlayView {
  return {
    drafts: { 0: { amount: 0, locked: false }, 1: { amount: 0, locked: false },
      2: { amount: 0, locked: false }, 3: { amount: 0, locked: false } },
    revealProgress: 0,
    showScoreboard: false,
    inspect: null,
    eventProgress: 0,
  };
}

/** Dim the whole screen behind an overlay. */
function scrim(ctx: CanvasRenderingContext2D, w: number, h: number, alpha = 0.72): void {
  ctx.save();
  ctx.fillStyle = `rgba(4, 5, 10, ${alpha})`;
  ctx.fillRect(0, 0, w, h);
  ctx.restore();
}

export class Overlays {
  private time = 0;

  update(dtMs: number): void {
    this.time += dtMs;
  }

  /* ------------------------------------------------------------- event card */

  /** The Drift event, presented as a dealt card. */
  drawEventCard(
    ctx: CanvasRenderingContext2D,
    state: GameState,
    view: OverlayView,
    w: number,
    h: number,
  ): void {
    const id = state.currentEvent;
    if (!id) return;
    const card = eventCard(id);
    const t = Math.max(0, Math.min(1, view.eventProgress));
    const slide = ease.outBack(Math.min(1, t * 1.4));

    scrim(ctx, w, h, 0.62 * Math.min(1, t * 2));

    const cardW = Math.min(620, w * 0.62);
    const cardH = 290;
    const x = w / 2 - cardW / 2;
    const y = h / 2 - cardH / 2 - 20 + (1 - slide) * 60;

    const tone = card.tone === 'good' ? INK.good : card.tone === 'bad' ? INK.bad : INK.warn;

    ctx.save();
    ctx.globalAlpha = Math.min(1, t * 2);

    panel(ctx, x, y, cardW, cardH, {
      fill: 'rgba(10, 12, 22, 0.97)',
      border: withAlpha(tone, 0.85),
      borderWidth: 2.5,
      accent: tone,
      accentWidth: 6,
      glow: withAlpha(tone, 0.45),
      chamfer: 24,
    });

    text(ctx, 'DRIFT EVENT', x + 34, y + 40, {
      size: TYPE.micro, weight: 800, color: INK.textFaint, tracking: 3.4, baseline: 'middle',
    });
    chip(ctx, card.tone, x + cardW - 34 - 78, y + 28, {
      color: tone, filled: true, size: TYPE.micro, height: 24,
    });

    text(ctx, card.name, x + 34, y + 92, {
      size: TYPE.title, weight: 800, color: INK.text, baseline: 'middle',
      maxWidth: cardW - 68, shadow: withAlpha(tone, 0.6),
    });

    paragraph(ctx, card.blurb, x + 34, y + 140, cardW - 68, {
      size: TYPE.body, weight: 500, color: INK.textDim, lineHeight: 27,
    });

    text(ctx, `Round ${state.round} of ${state.settings.totalRounds}`, x + 34, y + cardH - 32, {
      size: TYPE.label, weight: 700, color: INK.textFaint, baseline: 'middle',
    });
    hintBar(ctx, [{ button: 'a', label: 'Continue' }], x + cardW - 190, y + cardH - 32);
    ctx.restore();
  }

  /* --------------------------------------------------------------- bid screen */

  /**
   * The simultaneous sealed bid.
   *
   * Four columns, one per participating seat. A seat that has locked in shows a sealed
   * plate rather than its number — hiding the amount is the entire mechanic. Non-
   * participants are dimmed out so nobody reaches for a controller that is not in play.
   */
  drawBid(
    ctx: CanvasRenderingContext2D,
    state: GameState,
    view: OverlayView,
    w: number,
    h: number,
  ): void {
    const bid = state.activeBid;
    if (!bid) return;

    scrim(ctx, w, h, 0.82);

    const isContest = bid.kind === 'contest';
    const accent = isContest ? INK.bad : INK.warn;

    // Header: what is being fought over.
    const headerY = h * 0.14;
    text(ctx, isContest ? 'CONTEST' : 'CHARTER AUCTION', w / 2, headerY, {
      size: TYPE.heading, weight: 800, color: accent, align: 'center', baseline: 'middle',
      tracking: 5, shadow: withAlpha(accent, 0.7),
    });

    const subtitle = isContest
      ? this.contestSubtitle(state, bid.systemId)
      : bid.charterId
        ? `${CHARTERS[bid.charterId].name} — ${CHARTERS[bid.charterId].blurb} (${CHARTERS[bid.charterId].vp} VP)`
        : '';
    paragraph(ctx, subtitle, w / 2 - Math.min(760, w * 0.7) / 2, headerY + 38,
      Math.min(760, w * 0.7), {
        size: TYPE.body, weight: 500, color: INK.textDim, align: 'center', lineHeight: 26,
      });

    text(
      ctx,
      isContest
        ? 'Bid Fuel. Strength = fleets×2 + bid + defender bonus. Fuel is spent either way.'
        : 'Bid Data. Highest bid takes the charter and pays it. Losers pay nothing.',
      w / 2, headerY + 96,
      { size: TYPE.label, weight: 600, color: INK.textFaint, align: 'center', baseline: 'middle' },
    );

    // Columns. Height is derived from the space the HUD actually leaves, reserving room
    // for the footer — deriving it from a viewport fraction printed the columns straight
    // over the seat panels at 900px tall.
    const columns = 4;
    const gap = 18;
    const colW = Math.min(280, (w * 0.86 - gap * (columns - 1)) / columns);
    const totalW = colW * columns + gap * (columns - 1);
    const startX = w / 2 - totalW / 2;
    const colY = Math.max(h * 0.3, headerY + 130);
    const FOOTER_H = 96;
    const colH = Math.max(200, HUD_LAYOUT.seatPanelTop(h) - FOOTER_H - colY);

    for (let seat = 0 as PlayerId; seat < 4; seat = (seat + 1) as PlayerId) {
      const x = startX + seat * (colW + gap);
      this.drawBidColumn(ctx, state, view, bid, seat, x, colY, colW, colH);
    }

    // Footer: who we are still waiting on, or the reveal prompt.
    const waiting = pendingBidders(state);
    const footerY = colY + colH + 46;

    if (!bid.revealed) {
      const names = waiting.map((s) => state.players[s]!.name).join(', ');
      const pulse = ease.pulse((this.time % 1400) / 1400);
      ctx.save();
      ctx.globalAlpha = 0.6 + pulse * 0.4;
      text(ctx, waiting.length > 0 ? `Waiting on ${names}` : 'All bids locked', w / 2, footerY, {
        size: TYPE.body, weight: 700, color: INK.text, align: 'center', baseline: 'middle',
      });
      ctx.restore();

      hintBar(
        ctx,
        [
          { button: 'lt', label: 'Lower' },
          { button: 'rt', label: 'Raise' },
          { button: 'a', label: 'Lock in' },
        ],
        w / 2 - 150, footerY + 40,
      );
    } else if (bid.outcome) {
      const outcome = bid.outcome;
      const color = outcome.winner !== null ? seatColor(outcome.winner) : INK.textDim;
      text(ctx, outcome.note, w / 2, footerY, {
        size: TYPE.heading, weight: 800, color, align: 'center', baseline: 'middle',
        shadow: withAlpha(color, 0.6),
      });
      hintBar(ctx, [{ button: 'a', label: 'Continue' }], w / 2 - 60, footerY + 44);
    }
  }

  private contestSubtitle(state: GameState, systemId: HexKey | null): string {
    if (!systemId) return '';
    const system = systemOf(state, systemId);
    if (!system) return systemId;
    const cls = SYSTEM_CLASSES[system.cls].name;
    const owner = system.owner !== null ? state.players[system.owner]!.name : 'unclaimed';
    return `${cls} at ${systemId} — currently ${owner}`;
  }

  private drawBidColumn(
    ctx: CanvasRenderingContext2D,
    state: GameState,
    view: OverlayView,
    bid: BidRound,
    seat: PlayerId,
    x: number,
    y: number,
    w: number,
    h: number,
  ): void {
    const player = state.players[seat]!;
    const participating = bid.participants.includes(seat);
    const color = seatColor(seat);
    const draft = view.drafts[seat];
    const locked = bid.bids[seat] !== undefined;

    ctx.save();
    if (!participating) ctx.globalAlpha = 0.22;

    const pulse = ease.pulse((this.time % 1500) / 1500);
    panel(ctx, x, y, w, h, {
      fill: participating ? withAlpha(color, 0.1) : 'rgba(10,12,22,0.8)',
      border: participating
        ? locked ? withAlpha(INK.good, 0.9) : withAlpha(color, 0.55 + pulse * 0.4)
        : INK.edge,
      borderWidth: participating ? 2.4 : 1.2,
      accent: color,
      accentWidth: 5,
      glow: participating && !locked ? withAlpha(color, 0.32) : undefined,
      chamfer: 16,
    });

    // Header.
    seatBadge(ctx, seat, x + 26, y + 30, 13, { label: PLAYER_SHORT[seat] });
    text(ctx, player.name, x + 48, y + 30, {
      size: TYPE.label, weight: 800, color: INK.text, baseline: 'middle', maxWidth: w - 70,
    });

    if (!participating) {
      text(ctx, 'not in this bid', x + w / 2, y + h / 2, {
        size: TYPE.label, color: INK.textFaint, align: 'center', baseline: 'middle',
      });
      ctx.restore();
      return;
    }

    // Stock of the bid resource.
    const stock = player.resources[bid.resource];
    text(ctx, `${bid.resource.toUpperCase()} HELD`, x + 22, y + 62, {
      size: TYPE.micro, weight: 700, color: INK.textFaint, tracking: 1.6, baseline: 'middle',
    });
    text(ctx, String(stock), x + w - 22, y + 62, {
      size: TYPE.label, weight: 800, color: INK.text, align: 'right', baseline: 'middle',
    });

    // Fleet strength preview, contests only — the number players actually reason about.
    if (bid.kind === 'contest' && bid.systemId) {
      const fleets = fleetsAtOwnedBy(state, bid.systemId, seat).length;
      const system = systemOf(state, bid.systemId);
      const defender = system && system.owner === seat ? 1 : 0;
      const base = fleets * 2 + defender;
      text(ctx, `${fleets} fleet${fleets === 1 ? '' : 's'} → base ${base}`, x + 22, y + 88, {
        size: TYPE.micro, weight: 600, color: INK.info, baseline: 'middle',
      });
    }

    // The dial.
    const dialY = y + h * 0.52;
    if (bid.revealed) {
      const revealed = bid.bids[seat] ?? 0;
      const strength = bid.outcome?.strengths[seat];
      const won = bid.outcome?.winner === seat;

      text(ctx, String(revealed), x + w / 2, dialY, {
        size: TYPE.hero * 0.8, weight: 800,
        color: won ? INK.good : INK.text,
        align: 'center', baseline: 'middle',
        shadow: withAlpha(won ? INK.good : color, 0.7),
      });
      if (strength !== undefined) {
        text(ctx, `strength ${strength}`, x + w / 2, dialY + 54, {
          size: TYPE.label, weight: 700, color: INK.textDim, align: 'center', baseline: 'middle',
        });
      }
      if (won) {
        chip(ctx, 'winner', x + w / 2 - 34, dialY + 74, {
          color: INK.good, filled: true, size: TYPE.micro, height: 24,
        });
      }
    } else if (locked) {
      // Sealed plate — the whole point is that the number is not visible.
      const plateW = w - 52;
      const plateH = 72;
      roundRect(ctx, x + 26, dialY - plateH / 2, plateW, plateH, 12);
      ctx.fillStyle = withAlpha(INK.good, 0.16);
      ctx.fill();
      ctx.strokeStyle = withAlpha(INK.good, 0.8);
      ctx.lineWidth = 2;
      ctx.setLineDash([8, 6]);
      ctx.stroke();
      ctx.setLineDash([]);

      text(ctx, '🔒 SEALED', x + w / 2, dialY, {
        size: TYPE.body, weight: 800, color: INK.good, align: 'center', baseline: 'middle',
        tracking: 2,
      });
    } else {
      const amount = draft?.amount ?? 0;
      text(ctx, String(amount), x + w / 2, dialY, {
        size: TYPE.hero * 0.8, weight: 800, color, align: 'center', baseline: 'middle',
        shadow: withAlpha(color, 0.6),
      });
      // A track showing the dial position relative to stock.
      meter(ctx, x + 26, dialY + 48, w - 52, 9, amount, Math.max(1, stock), { color });
      text(ctx, `of ${stock}`, x + w / 2, dialY + 72, {
        size: TYPE.micro, weight: 600, color: INK.textFaint, align: 'center', baseline: 'middle',
      });
    }

    // AI seats show a thinking indicator rather than a dial they never touch.
    if (player.kind === 'ai' && !locked && !bid.revealed) {
      const dots = 1 + Math.floor((this.time / 320) % 3);
      text(ctx, `Autarch deciding${'.'.repeat(dots)}`, x + w / 2, y + h - 26, {
        size: TYPE.micro, weight: 600, color: INK.textFaint, align: 'center', baseline: 'middle',
      });
    }
    ctx.restore();
  }

  /* ------------------------------------------------------------ region scoring */

  /** Region majority award summary, shown at rounds 4 and 8. */
  drawRegionScoring(
    ctx: CanvasRenderingContext2D,
    state: GameState,
    w: number,
    h: number,
  ): void {
    scrim(ctx, w, h, 0.8);

    text(ctx, 'REGION MAJORITIES', w / 2, h * 0.16, {
      size: TYPE.heading, weight: 800, color: '#ffd76b', align: 'center', baseline: 'middle',
      tracking: 4.5,
    });

    const regions = ['core', 'midRim', 'outerRim'] as const;
    const cardW = Math.min(340, (w * 0.86 - 40) / 3);
    const totalW = cardW * 3 + 40;
    const startX = w / 2 - totalW / 2;
    const y = h * 0.26;
    const cardH = Math.max(240, HUD_LAYOUT.seatPanelTop(h) - 80 - y);

    regions.forEach((region, index) => {
      const def = REGIONS[region];
      const standing = regionStanding(state, region);
      const x = startX + index * (cardW + 20);

      panel(ctx, x, y, cardW, cardH, {
        fill: withAlpha(def.tint, 0.08),
        border: withAlpha(def.tint, 0.6),
        borderWidth: 2,
        accent: def.tint,
        chamfer: 16,
      });

      text(ctx, def.name, x + 22, y + 34, {
        size: TYPE.body, weight: 800, color: def.tint, baseline: 'middle',
      });
      text(ctx, `1st +${def.majorityVp}   2nd +${def.runnerUpVp}`, x + 22, y + 60, {
        size: TYPE.micro, weight: 700, color: INK.textFaint, baseline: 'middle',
      });

      // Seat holdings, sorted descending.
      const rows = ([0, 1, 2, 3] as PlayerId[])
        .map((seat) => ({ seat, count: standing.counts[seat] }))
        .sort((a, b) => b.count - a.count);

      rows.forEach((row, rowIndex) => {
        const rowY = y + 92 + rowIndex * 40;
        const isLeader = standing.leaders.includes(row.seat);
        const isRunnerUp = standing.runnersUp.includes(row.seat) && standing.leaders.length === 1;

        seatBadge(ctx, row.seat, x + 32, rowY, 12, { label: PLAYER_SHORT[row.seat] });
        text(ctx, state.players[row.seat]!.name, x + 54, rowY, {
          size: TYPE.label, weight: 700,
          color: isLeader ? INK.text : INK.textDim,
          baseline: 'middle', maxWidth: cardW - 150,
        });
        text(ctx, String(row.count), x + cardW - 78, rowY, {
          size: TYPE.label, weight: 800, color: INK.text, align: 'right', baseline: 'middle',
        });

        const award = isLeader ? def.majorityVp : isRunnerUp ? def.runnerUpVp : 0;
        if (award > 0) {
          text(ctx, `+${award}`, x + cardW - 22, rowY, {
            size: TYPE.label, weight: 800, color: INK.good, align: 'right', baseline: 'middle',
          });
        }
      });
    });

    hintBar(ctx, [{ button: 'a', label: 'Continue' }], w / 2 - 60, y + cardH + 40);
  }

  /* --------------------------------------------------------------- inspect card */

  /** System detail card, opened with Y. Anchored to a screen point. */
  drawInspect(
    ctx: CanvasRenderingContext2D,
    state: GameState,
    systemId: HexKey,
    anchor: { x: number; y: number },
    w: number,
    h: number,
  ): void {
    const system = systemOf(state, systemId);
    if (!system) return;

    const cardW = 316;
    const cardH = system.surveyed ? 268 : 150;
    // Keep the card on screen regardless of where the hex is.
    const x = Math.max(18, Math.min(w - cardW - 18, anchor.x + 40));
    const y = Math.max(18, Math.min(h - cardH - 18, anchor.y - cardH / 2));

    const ownerColor = system.owner !== null ? seatColor(system.owner) : INK.edgeStrong;

    panel(ctx, x, y, cardW, cardH, {
      fill: 'rgba(9, 11, 20, 0.97)',
      border: withAlpha(ownerColor, 0.8),
      borderWidth: 2,
      accent: system.owner !== null ? ownerColor : undefined,
      glow: withAlpha(ownerColor, 0.3),
      chamfer: 16,
    });

    if (!system.surveyed) {
      text(ctx, 'UNCHARTED', x + 22, y + 40, {
        size: TYPE.body, weight: 800, color: INK.textDim, tracking: 3, baseline: 'middle',
      });
      paragraph(ctx, 'Survey this system to reveal its class, yield and hazards.',
        x + 22, y + 74, cardW - 44,
        { size: TYPE.label, weight: 500, color: INK.textFaint, lineHeight: 21 });
      text(ctx, systemId, x + cardW - 22, y + 40, {
        size: TYPE.micro, weight: 700, color: INK.textFaint, align: 'right', baseline: 'middle',
      });
      return;
    }

    const def = SYSTEM_CLASSES[system.cls];
    text(ctx, def.name, x + 22, y + 36, {
      size: TYPE.body, weight: 800, color: INK.text, baseline: 'middle', maxWidth: cardW - 100,
    });
    text(ctx, systemId, x + cardW - 22, y + 36, {
      size: TYPE.micro, weight: 700, color: INK.textFaint, align: 'right', baseline: 'middle',
    });
    chip(ctx, REGIONS[system.region].name, x + 20, y + 52, {
      color: REGIONS[system.region].tint, size: TYPE.micro - 1, height: 20,
    });

    // Yield.
    text(ctx, 'YIELD', x + 22, y + 100, {
      size: TYPE.micro, weight: 800, color: INK.textFaint, tracking: 2, baseline: 'middle',
    });
    if (system.cls === 'deepSpace') {
      text(ctx, 'none', x + 84, y + 100, {
        size: TYPE.label, color: INK.textFaint, baseline: 'middle',
      });
    } else {
      drawYieldPips(ctx, x + 130, y + 100, 8, system.yield);
      if (system.development === 'colony') {
        text(ctx, '×2 colony', x + cardW - 22, y + 100, {
          size: TYPE.micro, weight: 700, color: INK.good, align: 'right', baseline: 'middle',
        });
      }
    }

    // Habitability + hazard.
    text(ctx, 'HABITABILITY', x + 22, y + 132, {
      size: TYPE.micro, weight: 800, color: INK.textFaint, tracking: 2, baseline: 'middle',
    });
    drawHabitability(ctx, x + 168, y + 132, 5, system.habitability);

    text(ctx, 'HAZARD', x + 22, y + 162, {
      size: TYPE.micro, weight: 800, color: INK.textFaint, tracking: 2, baseline: 'middle',
    });
    if (system.hazard === 0) {
      text(ctx, 'clear', x + 120, y + 162, {
        size: TYPE.micro, weight: 700, color: INK.good, baseline: 'middle',
      });
    } else {
      // Horizontal row of chevrons reads better in a card than the stacked board version.
      for (let i = 0; i < system.hazard; i++) {
        drawHazard(ctx, x + 124 + i * 18, y + 162, 6, 1);
      }
      text(ctx, `+${system.hazard} Ore to claim`, x + cardW - 22, y + 162, {
        size: TYPE.micro, weight: 700, color: INK.warn, align: 'right', baseline: 'middle',
      });
    }

    // Owner / development.
    text(ctx, 'STATUS', x + 22, y + 196, {
      size: TYPE.micro, weight: 800, color: INK.textFaint, tracking: 2, baseline: 'middle',
    });
    const status = system.owner === null
      ? 'unclaimed'
      : `${state.players[system.owner]!.name} · ${system.development}`;
    text(ctx, status, x + 100, y + 196, {
      size: TYPE.label, weight: 700, color: system.owner !== null ? ownerColor : INK.textDim,
      baseline: 'middle', maxWidth: cardW - 122,
    });

    // Relic.
    if (system.relic) {
      const relic = RELICS[system.relic];
      drawRelic(ctx, x + 34, y + 230, 11, system.relic, system.relicTaken);
      text(
        ctx,
        system.relicTaken ? `${relic.name} (recovered)` : `${relic.name} — ${relic.blurb}`,
        x + 56, y + 230,
        {
          size: TYPE.micro, weight: 600,
          color: system.relicTaken ? INK.textFaint : '#ffd76b',
          baseline: 'middle', maxWidth: cardW - 78,
        },
      );
    }

    if (isBlocked(system, state.round)) {
      text(ctx, '⚠ Producing nothing this round', x + 22, y + cardH - 16, {
        size: TYPE.micro, weight: 700, color: INK.warn, baseline: 'middle',
      });
    }
  }

  /* ---------------------------------------------------------------- scoreboard */

  /** Live scoreboard, toggled with Back. */
  drawScoreboard(
    ctx: CanvasRenderingContext2D,
    state: GameState,
    w: number,
    h: number,
  ): void {
    scrim(ctx, w, h, 0.86);

    const standings = finalStandings(state);
    const cardW = Math.min(880, w * 0.8);
    const x = w / 2 - cardW / 2;
    const y = h * 0.12;
    const rowH = 76;
    const cardH = 108 + standings.length * rowH;

    panel(ctx, x, y, cardW, cardH, {
      fill: 'rgba(9, 11, 20, 0.97)',
      border: INK.edgeStrong,
      borderWidth: 2,
      chamfer: 22,
    });

    text(ctx, 'STANDINGS', x + 32, y + 44, {
      size: TYPE.heading, weight: 800, color: INK.text, baseline: 'middle', tracking: 3.5,
    });
    text(ctx, `Round ${state.round} / ${state.settings.totalRounds}`, x + cardW - 32, y + 44, {
      size: TYPE.label, weight: 700, color: INK.textFaint, align: 'right', baseline: 'middle',
    });

    standings.forEach((standing, index) => {
      this.drawStandingRow(ctx, state, standing, x + 24, y + 80 + index * rowH, cardW - 48, rowH);
    });

    hintBar(ctx, [{ button: 'back', label: 'Close' }], x + 32, y + cardH + 28);
  }

  private drawStandingRow(
    ctx: CanvasRenderingContext2D,
    state: GameState,
    standing: FinalStanding,
    x: number,
    y: number,
    w: number,
    h: number,
  ): void {
    const player = state.players[standing.seat]!;
    const color = seatColor(standing.seat);
    const leading = standing.rank === 1;

    roundRect(ctx, x, y, w, h - 8, 12);
    ctx.fillStyle = leading ? withAlpha(color, 0.14) : 'rgba(255,255,255,0.03)';
    ctx.fill();
    if (leading) {
      ctx.strokeStyle = withAlpha(color, 0.6);
      ctx.lineWidth = 1.6;
      ctx.stroke();
    }

    const midY = y + (h - 8) / 2;

    text(ctx, `${standing.rank}`, x + 26, midY, {
      size: TYPE.heading, weight: 800, color: leading ? '#ffd76b' : INK.textFaint,
      align: 'center', baseline: 'middle',
    });
    seatBadge(ctx, standing.seat, x + 62, midY, 14, { label: PLAYER_SHORT[standing.seat] });

    text(ctx, player.name, x + 88, midY - 10, {
      size: TYPE.body, weight: 800, color: INK.text, baseline: 'middle', maxWidth: w * 0.3,
    });
    resourceRow(ctx, player.resources, x + 88, midY + 14, { size: TYPE.micro, gap: 16 });

    // Compact breakdown of the biggest contributors.
    const top = standing.breakdown.lines
      .filter((line) => line.vp > 0)
      .sort((a, b) => b.vp - a.vp)
      .slice(0, 3)
      .map((line) => `${line.label} ${line.vp}`)
      .join('  ·  ');
    text(ctx, top, x + w * 0.42, midY, {
      size: TYPE.micro, weight: 600, color: INK.textDim, baseline: 'middle',
      maxWidth: w * 0.36,
    });

    text(ctx, String(standing.vp), x + w - 26, midY, {
      size: TYPE.title * 0.8, weight: 800, color: INK.text, align: 'right', baseline: 'middle',
      shadow: withAlpha(color, 0.6),
    });
  }

  /* ------------------------------------------------------------- final results */

  /**
   * The endgame table. Full itemised breakdown per seat, because a euro-style game's
   * scoring reveal is part of the fun — players want to see where the points came from.
   */
  drawResults(
    ctx: CanvasRenderingContext2D,
    state: GameState,
    w: number,
    h: number,
    progress: number,
  ): void {
    scrim(ctx, w, h, 0.93);

    const standings = finalStandings(state);
    const winner = standings.find((s) => s.rank === 1);
    const winnerColor = winner ? seatColor(winner.seat) : INK.text;

    // Title.
    const titleY = h * 0.1;
    text(ctx, state.winner !== null ? 'VICTORY' : 'A SHARED CLAIM', w / 2, titleY, {
      size: TYPE.hero * 0.7, weight: 800, color: winnerColor, align: 'center', baseline: 'middle',
      tracking: 8, shadow: withAlpha(winnerColor, 0.8),
    });
    if (state.winner !== null) {
      text(ctx, state.players[state.winner]!.name, w / 2, titleY + 52, {
        size: TYPE.heading, weight: 700, color: INK.text, align: 'center', baseline: 'middle',
        tracking: 2,
      });
    }

    // Columns, one per seat, revealed in sequence so the table lands as a reveal.
    const colW = Math.min(300, (w * 0.9 - 36) / 4);
    const totalW = colW * 4 + 36;
    const startX = w / 2 - totalW / 2;
    const colY = h * 0.22;
    const colH = Math.max(300, h - colY - 90);

    standings.forEach((standing, index) => {
      const appear = Math.max(0, Math.min(1, progress * 4 - index * 0.6));
      if (appear <= 0) return;

      const x = startX + index * (colW + 12);
      ctx.save();
      ctx.globalAlpha = appear;
      ctx.translate(0, (1 - ease.outCubic(appear)) * 30);
      this.drawResultColumn(ctx, state, standing, x, colY, colW, colH);
      ctx.restore();
    });

    if (progress > 0.95) {
      hintBar(
        ctx,
        [{ button: 'a', label: 'Play again' }, { button: 'b', label: 'Main menu' }],
        w / 2 - 150, colY + colH + 34,
      );
    }
  }

  private drawResultColumn(
    ctx: CanvasRenderingContext2D,
    state: GameState,
    standing: FinalStanding,
    x: number, y: number, w: number, h: number,
  ): void {
    const player = state.players[standing.seat]!;
    const color = seatColor(standing.seat);
    const isWinner = standing.rank === 1;

    panel(ctx, x, y, w, h, {
      fill: isWinner ? withAlpha(color, 0.14) : 'rgba(9, 11, 20, 0.92)',
      border: isWinner ? withAlpha(color, 0.9) : INK.edge,
      borderWidth: isWinner ? 2.6 : 1.4,
      accent: color,
      accentWidth: 5,
      glow: isWinner ? withAlpha(color, 0.45) : undefined,
      chamfer: 16,
    });

    // Header.
    seatBadge(ctx, standing.seat, x + 26, y + 32, 13, { label: PLAYER_SHORT[standing.seat] });
    text(ctx, player.name, x + 48, y + 26, {
      size: TYPE.label, weight: 800, color: INK.text, baseline: 'middle', maxWidth: w - 70,
    });
    text(ctx, player.kind === 'ai' ? 'Autarch' : 'Human', x + 48, y + 43, {
      size: TYPE.micro, weight: 600, color: INK.textFaint, baseline: 'middle',
    });

    // Big total.
    text(ctx, String(standing.vp), x + w / 2, y + 92, {
      size: TYPE.hero * 0.62, weight: 800, color: isWinner ? '#ffd76b' : INK.text,
      align: 'center', baseline: 'middle', shadow: withAlpha(color, 0.7),
    });
    text(ctx, `RANK ${standing.rank}`, x + w / 2, y + 128, {
      size: TYPE.micro, weight: 800, color: INK.textFaint, align: 'center', baseline: 'middle',
      tracking: 2.4,
    });

    // Itemised lines.
    let rowY = y + 158;
    const maxY = y + h - 56;
    for (const line of standing.breakdown.lines) {
      if (rowY > maxY) break;
      const dim = line.vp === 0;
      text(ctx, line.label, x + 20, rowY, {
        size: TYPE.micro, weight: 600, color: dim ? INK.textFaint : INK.textDim,
        baseline: 'middle', maxWidth: w - 78,
      });
      text(ctx, line.vp > 0 ? `+${line.vp}` : '—', x + w - 20, rowY, {
        size: TYPE.micro, weight: 800, color: dim ? INK.textFaint : INK.good,
        align: 'right', baseline: 'middle',
      });
      rowY += 21;
    }

    // Footer stats — the "how did I play" line.
    const stats = player.stats;
    text(
      ctx,
      `${stats.systemsSurveyed} surveyed · ${stats.coloniesBuilt} colonies · ${stats.contestsWon}W/${stats.contestsLost}L`,
      x + 20, y + h - 24,
      { size: TYPE.micro - 1, weight: 600, color: INK.textFaint, baseline: 'middle', maxWidth: w - 40 },
    );
  }

  /** Static describe helper, shared with the log. */
  static describeSystem(system: StarSystem): string {
    if (!system.surveyed) return 'Uncharted system';
    return `${SYSTEM_CLASSES[system.cls].name} (hab ${system.habitability}, hazard ${system.hazard})`;
  }
}
